/**
 * HTTP surface for voice and video calls — protocol/calls-v1.md.
 */

import { api, isApiError } from '@/api/client'
import type { Call, CallDeviceKind, CallMedia, UUID } from '@/api/types'
import { randomUuid } from '@/lib/uuid'

export interface IceServersResponse {
  ice_servers: RTCIceServer[]
  expires_at: string
}

/** What this client calls itself when it takes a seat (calls-v1 §7). */
export const CALL_DEVICE_KIND: CallDeviceKind = 'web'

/**
 * One device's part in a call. Made fresh for every attempt to join, so the
 * server can tell a reconnect (the same id) from another device taking the
 * call over (a different one).
 */
export function newCallSessionId(): string {
  return randomUuid()
}

/** A call entered, and the session the server holds this device to. */
export interface CallEntry {
  call: Call
  sessionId: string
}

interface CallEntryResponse {
  call: Call
  session_id?: string | null
}

// A server older than sessions answers without one. The id we sent is then
// the only one there is, and keeping it costs nothing: that server ignores it.
function entered(body: CallEntryResponse, sent: string): CallEntry {
  const named = body.session_id
  return { call: body.call, sessionId: typeof named === 'string' && named !== '' ? named : sent }
}

export async function fetchIceServers(): Promise<IceServersResponse> {
  return api.get<IceServersResponse>('/calls/ice-servers')
}

export async function fetchActiveCall(roomId: UUID): Promise<Call | null> {
  const body = await api.get<{ call: Call | null }>(`/rooms/${roomId}/calls/active`)
  return body.call
}

/**
 * The call as the server has it now, or null once there is nothing to have.
 *
 * `GET /calls/:id` came with sessions. A server without it answers 404, and
 * the room's active call is the nearest thing it can say: the same call while
 * it runs, and something else — or nothing — once it is over.
 */
export async function fetchCall(callId: UUID, roomId: UUID): Promise<Call | null> {
  try {
    const body = await api.get<{ call: Call }>(`/calls/${callId}`)
    return body.call
  } catch (caught) {
    if (!isApiError(caught) || caught.status !== 404) throw caught
    const active = await fetchActiveCall(roomId)
    return active !== null && active.id === callId ? active : null
  }
}

export async function startCall(roomId: UUID, media: CallMedia, sessionId: string): Promise<CallEntry> {
  const body = await api.post<CallEntryResponse>(`/rooms/${roomId}/calls`, {
    media,
    session_id: sessionId,
    device_kind: CALL_DEVICE_KIND,
  })
  return entered(body, sessionId)
}

/**
 * Joining says what this client can render. A client that cannot draw a second
 * video track must never be sent one — it keys remote media by user, so a
 * screen would replace the camera it is already showing. See
 * protocol/calls-v2-screen-sharing.md.
 */
export const CLIENT_CALL_FEATURES = ['screen_share'] as const

export async function joinCall(
  callId: UUID,
  sessionId: string,
  features: readonly string[] = CLIENT_CALL_FEATURES,
): Promise<CallEntry> {
  const body = await api.post<CallEntryResponse>(`/calls/${callId}/join`, {
    session_id: sessionId,
    device_kind: CALL_DEVICE_KIND,
    features,
  })
  return entered(body, sessionId)
}

export async function declineCall(callId: UUID): Promise<void> {
  await api.post(`/calls/${callId}/decline`)
}

/**
 * Leaving as a session, so a device the call has already moved away from
 * cannot hang up on the one it moved to: the server ignores a leave from a
 * session that no longer holds the seat.
 */
export async function leaveCall(callId: UUID, sessionId: string | null): Promise<Call | null> {
  const body = await api.post<{ call: Call | null }>(
    `/calls/${callId}/leave`,
    sessionId === null ? {} : { session_id: sessionId },
  )
  return body.call
}

/** Leaving as the tab closes: a request the page is allowed to outlive. */
export function leaveCallOnUnload(callId: UUID, sessionId: string): void {
  api.keepalive(`/calls/${callId}/leave`, { session_id: sessionId })
}
